import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Must stay in sync with the header height in Header.tsx
const HEADER_HEIGHT = 80;
// Max the user can drag to — leaves a thin clickable overlay strip at the bottom
const MAX_PANEL_RATIO = 0.92;
const HANDLE_HEIGHT = 40;
const TRANSFORM_TRANSITION_MS = 350;
const HEIGHT_TRANSITION_MS = 350;

interface SlideOutPanelProps {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  leftOffset?: number;
}

/**
 * SlideOutPanel slides DOWN from the bottom edge of the header.
 *
 * The panel uses an explicit pixel height derived from a ResizeObserver on the
 * content, so it can smoothly transition when the content's natural size changes
 * (e.g. switching between Personal / Global tabs). CSS can't animate to/from
 * `height: auto`, so we track the measured content height in state and animate
 * that instead.
 *
 * Content wrapper flex behavior:
 *   • At natural height (dragMinHeight === 0): `flex: 0 0 auto` — wrapper hugs
 *     its intrinsic size so ResizeObserver reports the true content height.
 *   • When dragged taller (dragMinHeight > 0): `flex: 1 0 auto` — wrapper grows
 *     to fill the remaining panel space so children with `h-full` expand with
 *     the drag.
 *
 * The wrapper reserves its bottom `HANDLE_HEIGHT` pixels as padding, and the
 * drag handle is absolutely positioned at the panel's bottom edge. This keeps
 * the handle visible during height transitions: when switching to a taller tab
 * the wrapper's new intrinsic size briefly exceeds the animating panel height,
 * and an in-flex-flow handle would be clipped by `overflow: hidden`. An
 * absolute handle stays pinned to the visible panel bottom in every frame.
 */
export const SlideOutPanel: React.FC<SlideOutPanelProps> = ({ isOpen, onClose, children, leftOffset = 0 }) => {
  // Persistent height floor for the panel. 0 means "no floor" (follow content).
  // Set by direct drag OR by the freeze-on-shrink effect below so switching
  // to a shorter tab never collapses the panel.
  const [dragMinHeight, setDragMinHeight] = useState(0);
  // Mirrors isDraggingRef so we can disable the height transition while dragging
  const [isDragging, setIsDragging] = useState(false);
  // Natural (unclipped) height of the panel's content, measured live via ResizeObserver
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  const isDraggingRef = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Previous contentHeight snapshot used by the freeze-on-shrink logic in
  // the ResizeObserver callback below.
  const prevContentHeightRef = useRef<number | null>(null);
  // Mirror of dragMinHeight, kept in sync via useLayoutEffect so the
  // ResizeObserver callback can read the latest committed value without
  // stale-closure issues.
  const dragMinHeightRef = useRef(0);

  useLayoutEffect(() => {
    dragMinHeightRef.current = dragMinHeight;
  }, [dragMinHeight]);

  // Reset when panel closes
  useEffect(() => {
    if (!isOpen) {
      setDragMinHeight(0);
      prevContentHeightRef.current = null;
    }
  }, [isOpen]);

  // Re-clamp dragMinHeight if the viewport shrinks
  useEffect(() => {
    const handle = () => {
      const max = (window.innerHeight - HEADER_HEIGHT) * MAX_PANEL_RATIO;
      setDragMinHeight(h => Math.min(h, max));
    };
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  // Track the panel's INTRINSIC height AND detect shrinks in one pass.
  //
  // `contentHeight` measures the wrapper's total rendered height (children
  // slot + handle spacer). Because the wrapper has no padding, the initial
  // `getBoundingClientRect().height` seed and subsequent `contentRect.height`
  // reports from ResizeObserver measure the same box — no spurious 40px
  // "shrink" between mount and the first RO fire.
  //
  // The ResizeObserver only processes fires when `dragMinHeight === 0` (hug
  // mode). In hug mode the wrapper is `flex: 0 0 auto`, so its rendered size
  // equals the panel's natural size. In filled mode (`flex: 1 0 auto`) the
  // wrapper's size instead reflects the panel's height, which is itself a
  // function of `dragMinHeight` — RO fires there just echo dragMinHeight
  // back and would fight the drag if we set state from them, producing
  // per-frame jitter as the pointer moves.
  //
  // Ignoring filled-mode fires also keeps `contentHeight` permanently equal
  // to the true natural height, which is what the pointer-move handler
  // needs for a stable "drag below natural → release" threshold.
  //
  // Batching setDragMinHeight and setContentHeight in the same callback
  // makes the tab-switch shrink render in one commit, so the panel never
  // paints an intermediate collapsed frame before the freeze bump retargets
  // it back to its previous height.
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const initial = el.getBoundingClientRect().height;
    setContentHeight(initial);
    prevContentHeightRef.current = initial;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      // Skip fires while the wrapper is filled — see block comment above.
      if (dragMinHeightRef.current !== 0) return;
      const newHeight = entry.contentRect.height;
      const prev = prevContentHeightRef.current;
      prevContentHeightRef.current = newHeight;
      // Freeze-on-shrink: pin dragMinHeight to at least the previous natural
      // height so a shorter incoming tab doesn't collapse the panel. Growing
      // content is handled naturally by the max() in `explicitHeight`.
      if (prev != null && newHeight < prev) {
        setDragMinHeight(current => Math.max(current, prev));
      }
      setContentHeight(newHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Pointer handlers ───────────────────────────────────────────────────────
  // setPointerCapture routes all pointermove/pointerup to this element even
  // after the cursor moves outside it, so the drag is always tracked.

  const onHandlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onHandlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const max = (window.innerHeight - HEADER_HEIGHT) * MAX_PANEL_RATIO;
    const desired = e.clientY - HEADER_HEIGHT;
    // `contentHeight` already includes the in-flow handle spacer, so it IS
    // the natural panel size. Dragging at or above it releases the floor
    // entirely so the panel can auto-fit content again.
    const natural = contentHeight ?? 0;
    if (desired <= natural) {
      // Releasing the floor causes the wrapper to flip from "fill" back to
      // "hug intrinsic", which the ResizeObserver will report as a shrink.
      // Clear the previous-height baseline so the freeze logic doesn't
      // misinterpret that mode-flip as a tab-switch shrink and re-freeze.
      prevContentHeightRef.current = null;
      setDragMinHeight(0);
    } else {
      setDragMinHeight(Math.min(desired, max));
    }
  }, [contentHeight]);

  const onHandlePointerUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  // Explicit panel height = whichever is bigger of the user's height floor
  // (dragMinHeight) and the panel's natural height (measured from the
  // wrapper, which includes the in-flow handle spacer). This unifies three
  // behaviors in one formula:
  //   • Auto-fit on mount / when content grows: max() picks natural.
  //   • Drag-to-resize taller: max() picks dragMinHeight.
  //   • Tab switch to a shorter tab: the freeze effect above bumps
  //     dragMinHeight, so max() picks the frozen height (no collapse).
  const explicitHeight = contentHeight != null
    ? Math.max(dragMinHeight, contentHeight)
    : undefined;

  // Skip the height transition while actively dragging so the panel tracks the
  // pointer 1:1 without a trailing animation.
  const transitionStyle = isDragging
    ? `transform ${TRANSFORM_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`
    : `transform ${TRANSFORM_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1), height ${HEIGHT_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;

  return (
    <>
      {/* Dim overlay — covers area below header to the right of menu, click to close */}
      <div
        className={`fixed right-0 bottom-0 z-[104] transition-opacity duration-300 bg-black/40 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ top: HEADER_HEIGHT, left: leftOffset }}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Clip container — anchored at the header bottom edge, hides the panel when it slides up */}
      <div
        className="fixed right-0 z-[105]"
        style={{
          top: HEADER_HEIGHT,
          left: leftOffset,
          maxHeight: `${MAX_PANEL_RATIO * 100}vh`,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
      {/* Panel.
           `minHeight: 'max-content'` is a CSS-level safety net that guarantees
           the panel is never rendered shorter than its intrinsic content,
           regardless of what the JS-computed `explicitHeight` says. This
           matters because in filled mode we intentionally ignore
           ResizeObserver fires (see the block comment on the RO effect),
           which can leave `contentHeight` stale. If the true intrinsic grows
           while stale — e.g. auto-refresh, tab switch, or the chart
           re-laying itself out at a new width — the wrapper's `flex-shrink: 0`
           keeps it at the new (larger) intrinsic and it would otherwise
           overflow the panel and hide bottom-anchored content like the
           Personal/Global toggle. With `max-content` the panel expands to
           fit instead of clipping. */}
      <div
        ref={panelRef}
        className="flex flex-col w-full"
        style={{
          height: explicitHeight ?? 'auto',
          minHeight: 'max-content',
          maxHeight: `${MAX_PANEL_RATIO * 100}vh`,
          background: 'var(--color-panel-bg, #18181b)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.45)',
          transform: isOpen ? 'translateY(0)' : 'translateY(-100%)',
          transition: transitionStyle,
          overflow: 'hidden',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        role="dialog"
        aria-modal="true"
      >
        <button
          className="absolute top-4 right-4 z-20 text-gray-400 hover:text-gray-200 focus:outline-none text-2xl leading-none"
          onClick={onClose}
          aria-label="Close panel"
        >
          x
        </button>

        {/* Content wrapper.
             • dragMinHeight === 0 (auto-fit): `flex: 0 0 auto` — wrapper hugs
               its intrinsic size so ResizeObserver reports the true natural
               panel height (children + handle spacer).
             • dragMinHeight > 0 (dragged or freeze-pinned): `flex: 1 0 auto`
               — wrapper grows to fill the extra panel space so children
               with `h-full` expand along with it.
             The gate is `dragMinHeight > 0` (a stable state value) rather
             than a computed comparison against `contentHeight`. This is
             critical: gating on `dragMinHeight > naturalHeight` would let
             the wrapper's flex mode depend on `contentHeight`, which in
             turn depends on the wrapper's mode — creating a feedback loop
             where the wrapper oscillates between intrinsic and filled sizes
             on every ResizeObserver fire.
             The wrapper is itself a flex-column with two items:
               1. A children slot (`flex: 1 1 auto, minHeight: 0`) that
                  contains the caller's content. In hug mode the slot sizes
                  to the children's intrinsic height; in filled mode it
                  grows to consume the extra panel space so `h-full`
                  children expand.
               2. A bottom spacer (`flex: 0 0 auto, height: HANDLE_HEIGHT`)
                  that reserves in-flow room for the absolutely-positioned
                  drag handle. Using an in-flow spacer (instead of the
                  earlier `paddingBottom` on the wrapper) means children
                  with `h-full` resolve their size against the slot — NOT
                  the padding region — so bottom-anchored content like the
                  Personal/Global toggle can never get pushed behind the
                  handle. */}
        <div
          ref={contentRef}
          style={{
            flex: dragMinHeight > 0 ? '1 0 auto' : '0 0 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {children}
          </div>
          <div style={{ flex: '0 0 auto', height: HANDLE_HEIGHT }} aria-hidden="true" />
        </div>

        {/* Drag handle — absolutely positioned at the panel's bottom edge so
             it stays visible during height transitions. When the panel is
             mid-animation and the wrapper's new intrinsic size (e.g. after
             switching to a taller tab) briefly exceeds the panel's current
             height, an in-flex-flow handle would be clipped by
             `overflow: hidden`; an absolute handle stays pinned to the visible
             panel bottom instead. */}
        <div
          className="absolute bottom-0 left-0 right-0 z-10 flex cursor-row-resize items-center justify-center select-none"
          style={{ height: HANDLE_HEIGHT, touchAction: 'none', background: 'transparent' }}
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
          aria-hidden="true"
        >
          <div className="rounded-full" style={{ width: 48, height: 4, background: 'rgba(255,255,255,0.35)' }} />
        </div>
      </div>
      </div>
    </>
  );
};
